import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Replay as RetryIcon } from '@material-ui/icons';
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now';
import format from 'date-fns/format';

import type { AnalyticsExecution, AnalyticsScheduleView } from '../types';
import { StatusChip } from './StatusChips';
import { formatDuration, formatBytes, normalizeStatus } from '../utils';

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  tableWrapper: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
}));

interface ExecutionsTableProps {
  executions: AnalyticsExecution[];
  schedules: AnalyticsScheduleView[];
  loading: boolean;
  daysBack: number;
  onRetry: (executionId: number) => void;
}

export const ExecutionsTable: React.FC<ExecutionsTableProps> = ({
  executions,
  schedules,
  loading,
  daysBack,
  onRetry,
}) => {
  const classes = useStyles();

  const getOrgName = (orgId: number): string => {
    const schedule = schedules.find(s => s.orgId === orgId);
    return schedule?.orgName || `Org #${orgId}`;
  };

  if (loading) {
    return (
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (executions.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            No executions found in the last {daysBack} days with the current filters.
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Use the Trigger button on the Schedules tab to run a report.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <TableContainer className={classes.tableWrapper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Triggered By</TableCell>
              <TableCell>Error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.map(execution => (
              <ExecutionRow
                key={execution.id}
                execution={execution}
                orgName={getOrgName(execution.orgId)}
                onRetry={onRetry}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

interface ExecutionRowProps {
  execution: AnalyticsExecution;
  orgName: string;
  onRetry: (executionId: number) => void;
}

const ExecutionRow: React.FC<ExecutionRowProps> = ({ execution, orgName, onRetry }) => {
  const classes = useStyles();
  const normalizedStatus = normalizeStatus(execution.status);
  const isFailed = normalizedStatus === 'failed';

  return (
    <TableRow hover>
      <TableCell>{execution.id}</TableCell>
      <TableCell>{orgName}</TableCell>
      <TableCell>
        <Chip label={execution.reportType} size="small" variant="outlined" />
      </TableCell>
      <TableCell>
        <StatusChip status={execution.status} />
      </TableCell>
      <TableCell>
        <Tooltip title={format(new Date(execution.startedAt), 'PPpp')}>
          <span>{distanceInWordsToNow(new Date(execution.startedAt), { addSuffix: true })}</span>
        </Tooltip>
      </TableCell>
      <TableCell>{formatDuration(execution.durationMs)}</TableCell>
      <TableCell>{execution.recipientCount ?? '-'}</TableCell>
      <TableCell>{formatBytes(execution.pdfSizeBytes)}</TableCell>
      <TableCell>
        <Chip
          label={execution.triggeredBy}
          size="small"
          variant="outlined"
          color={execution.triggeredBy.toLowerCase() === 'manual' ? 'primary' : 'default'}
        />
      </TableCell>
      <TableCell>
        {execution.errorMessage && (
          <Tooltip title={execution.errorMessage}>
            <Typography className={classes.errorText}>{execution.errorMessage}</Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right">
        {isFailed && (
          <Tooltip title="Retry failed execution">
            <IconButton size="small" onClick={() => onRetry(execution.id)} className={classes.actionButton}>
              <RetryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};
